import { EdgeDeviceProvisionCommandOptions } from "./edgeDeviceInterfaces";

const DEFAULT_HOST = "<router-wan-ip>";
const DEFAULT_SSH_USER = "root";
// Both scripts refuse to run without a server, so never drop the flag.
const DEFAULT_WIREGUARD_SERVER = "<wireguard-server>";

// Same key as media/scripts/edge_device_provision_command.js in actuate_admin.
// The backend round-robin only advances when stage omits wireguard_server, and
// the scripts always send it, so rotation has to happen where the command is built.
const WIREGUARD_ROUND_ROBIN_KEY = "edge_device_staging_wg_rr_idx";

const readRoundRobinIndex = (): number => {
  try {
    return parseInt(localStorage.getItem(WIREGUARD_ROUND_ROBIN_KEY) || "0", 10) || 0;
  } catch {
    return 0;
  }
};

export const peekRoundRobinWireguardServer = (pool: string[]): string => {
  if (!pool.length) {
    return "";
  }
  const idx = ((readRoundRobinIndex() % pool.length) + pool.length) % pool.length;
  return pool[idx];
};

// Next tunnel gets the zone after the one just used, even if the user overrode it.
export const advanceRoundRobinWireguardServer = (pool: string[], used: string): void => {
  const usedIdx = pool.indexOf(used);
  if (usedIdx < 0) {
    return;
  }
  try {
    localStorage.setItem(WIREGUARD_ROUND_ROBIN_KEY, String((usedIdx + 1) % pool.length));
  } catch {
    // ignore quota / private mode
  }
};

const resolveOptions = (options: EdgeDeviceProvisionCommandOptions) => ({
  host: options.routerWanIp.trim() || DEFAULT_HOST,
  sshUser: options.sshUser.trim() || DEFAULT_SSH_USER,
  wireguardServer: options.wireguardServer.trim() || DEFAULT_WIREGUARD_SERVER,
  cameraSubnet: options.cameraLocalSubnet.trim(),
});

// Mirrors bench_provision_command_example in actuate_admin
// (inframap/sites/edge_device/bootstrap_scripts.py) and the admin command
// builder. Ask-pass and virtual subnets are always on for customers.
export const buildBashProvisionCommand = (
  provisionToken: string,
  options: EdgeDeviceProvisionCommandOptions
): string => {
  const { host, sshUser, wireguardServer, cameraSubnet } = resolveOptions(options);

  const args = [
    `--host ${host}`,
    `--ssh-user ${sshUser}`,
    "--ask-pass",
    `--provision-token '${provisionToken}'`,
    `--wireguard-server ${wireguardServer}`,
    "--use-virtual-subnets",
  ];
  if (cameraSubnet) {
    args.push(`--camera-local-subnet ${cameraSubnet}`);
  }

  let command = `unzip actuate-edge-device-bootstrap.zip\n\n./provision-wireguard.sh \\\n  ${args.join(
    " \\\n  "
  )}`;
  if (!cameraSubnet) {
    command +=
      "\n\n# Cameras on WAN/upstream (not network.lan) — NETMAP local side:\n" +
      "#   --camera-local-subnet 192.168.0.0/24";
  }
  return command;
};

export const buildPowershellProvisionCommand = (
  provisionToken: string,
  options: EdgeDeviceProvisionCommandOptions
): string => {
  const { host, sshUser, wireguardServer, cameraSubnet } = resolveOptions(options);

  const args = [
    `-RouterHost ${host}`,
    `-SshUser ${sshUser}`,
    `-ProvisionToken '${provisionToken}'`,
    `-WireguardServer ${wireguardServer}`,
    "-UseVirtualSubnets",
  ];
  if (cameraSubnet) {
    args.push(`-CameraLocalSubnet ${cameraSubnet}`);
  }

  let command = `.\\provision-wireguard.ps1 \`\n  ${args.join(" `\n  ")}`;
  if (!cameraSubnet) {
    command +=
      "\n\n# Cameras on WAN/upstream (not network.lan):\n" +
      "#   -CameraLocalSubnet 192.168.0.0/24";
  }
  return command;
};
